import React, { useEffect, useMemo, useRef, useState } from 'react';

import useClaimFieldsConfiguration from '~/components/ClaimPage/hooks/useClaimFieldsConfiguration';
import { isSidebarTabEnabledForOrganization } from '~/components/ClaimPage/hooks/useClaimSideBarTabs/utils';
import { CLAIM_FIELD_CONFIG_TYPE } from '~/components/ClaimPage/types';
import { MainCard, Text } from '~/components/core';
import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import type { DndOrderedListReorder } from '~/components/core/DndOrderedList/DndOrderedList.types';
import { FeedbackSnackbar, useSnackbar } from '~/components/core/Molecules/Snackbar';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { useCliveConfigOptions } from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';

import SidebarFieldConfigCard from './components/SidebarFieldConfigCard';
import { moveDisplayedField } from './apiService';
import { MOVE_TEXTS, SIDEBAR_TEXTS as TEXTS } from './texts';

const SidebarFieldsConfiguration = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { isCliveConfigEnabledForOrganization } = useCliveConfigOptions();
  // A ref (not state) so the re-entrant-reorder guard reads the latest value synchronously;
  // nothing renders off it, so it doesn't need to be state.
  const isSavingRef = useRef(false);
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();
  const { fieldsConfiguration, isError, reloadData } = useClaimFieldsConfiguration(
    CLAIM_FIELD_CONFIG_TYPE.CLAIM_SIDEBAR
  );
  const displayedFieldsConfiguration = useMemo(() => {
    // clive_insights is driven by the Clive organization configuration rather than a feature flag,
    // so it is resolved at organization level here - this screen has no claim and no sub-organization.
    return fieldsConfiguration.filter((field) =>
      field.field_key === 'clive_insights'
        ? isCliveConfigEnabledForOrganization('clive_insights_claim_tab')
        : isSidebarTabEnabledForOrganization(field.field_key, organization)
    );
  }, [fieldsConfiguration, organization, isCliveConfigEnabledForOrganization]);

  // Local order so a drop reorders instantly (optimistic); resynced to the server
  // whenever the fetched data changes, which also reverts a failed reorder.
  const [orderedFields, setOrderedFields] = useState(displayedFieldsConfiguration);
  useEffect(() => {
    setOrderedFields(displayedFieldsConfiguration);
  }, [displayedFieldsConfiguration]);

  const handleReorder = async ({ fromIndex, toIndex }: DndOrderedListReorder) => {
    if (isSavingRef.current) {
      showSnackbar(TEXTS.reorderWhileSaving, 'info');
      return;
    }
    isSavingRef.current = true;

    const reordered = [...orderedFields];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setOrderedFields(reordered);

    try {
      const outcome = await moveDisplayedField(
        organization?.id,
        CLAIM_FIELD_CONFIG_TYPE.CLAIM_SIDEBAR,
        fieldsConfiguration,
        orderedFields,
        fromIndex,
        toIndex
      );
      if (outcome === 'listChanged') {
        showSnackbar(MOVE_TEXTS.listChanged, 'warning');
      }
    } finally {
      // Resync to the server's order (reverts on failure) and release the slot even if it fails.
      try {
        await reloadData();
      } finally {
        isSavingRef.current = false;
      }
    }
  };

  return isError ? (
    <SkeletonTable rowsCount={12} columnsCount={1} isError={isError} />
  ) : (
    <MainCard type="elevated" title={TEXTS.sectionTitle}>
      <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="-mt-4 mb-12">
        {TEXTS.sectionDescription}
      </Text>
      <DndOrderedList
        className="flex flex-col gap-12"
        items={orderedFields}
        getItemId={(field) => field.field_key}
        renderItem={(field, { dragHandleProps }) => (
          <SidebarFieldConfigCard field={field} dragHandleProps={dragHandleProps} />
        )}
        onReorder={handleReorder}
      />
      <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
    </MainCard>
  );
};

export default SidebarFieldsConfiguration;
